import { useState, useCallback } from 'react'
import {
  updateQueueStatus,
  updateQueuePosition,
  removeFromQueue,
  updateLastCalledAt,
  updateLastFinishedAt,
  createQueueEntry,
  completeSession,
  createNoSaleReason,
  batchUpdateQueuePositions,
} from '../services'

export const useQueueActions = (pluginspaceId: number) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const changeStatus = useCallback(
    async (queueId: number, status: string) => {
      try {
        setLoading(true)
        setError(null)
        await updateQueueStatus(queueId, status)
        setLoading(false)
        return true
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao atualizar status')
        setLoading(false)
        return false
      }
    },
    []
  )

  const reorderQueue = useCallback(
    async (sellerId: number, newPosition: number) => {
      try {
        setLoading(true)
        setError(null)
        await updateQueuePosition(sellerId, newPosition, pluginspaceId)
        setLoading(false)
        return true
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao reordenar fila')
        setLoading(false)
        return false
      }
    },
    [pluginspaceId]
  )

  const removeSellerFromQueue = useCallback(
    async (sellerId: number) => {
      try {
        setLoading(true)
        setError(null)
        console.log(`[useQueueActions] removeSellerFromQueue: sellerId=${sellerId}`);
        await removeFromQueue(sellerId)
        setLoading(false)
        console.log(`[useQueueActions] ✅ Removal successful`);
        return true
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Erro ao remover da fila';
        console.error(`[useQueueActions] ❌ Removal failed:`, errorMsg);
        setError(errorMsg)
        setLoading(false)
        return false
      }
    },
    []
  )

  const callSeller = useCallback(async (queueId: number) => {
    try {
      setLoading(true)
      await updateLastCalledAt(queueId)
      setLoading(false)
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao chamar vendedor')
      setLoading(false)
      return false
    }
  }, [])

  const finishCall = useCallback(async (queueId: number) => {
    try {
      setLoading(true)
      await updateLastFinishedAt(queueId)
      setLoading(false)
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao finalizar atendimento')
      setLoading(false)
      return false
    }
  }, [])

  const addToQueue = useCallback(
    async (sellerId: number, position?: number) => {
      try {
        setLoading(true)
        setError(null)
        await createQueueEntry(pluginspaceId, sellerId, position)
        setLoading(false)
        return true
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao adicionar à fila')
        setLoading(false)
        return false
      }
    },
    [pluginspaceId]
  )

  const finishSession = useCallback(
    async (sessionId: number, outcome: string, durationSeconds: number) => {
      try {
        setLoading(true)
        setError(null)
        await completeSession(sessionId, outcome, durationSeconds)
        setLoading(false)
        return true
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao finalizar sessão')
        setLoading(false)
        return false
      }
    },
    []
  )

  const registerNoSaleReason = useCallback(
    async (
      sessionId: number,
      reasonCode: string,
      productName?: string,
      sizeWanted?: string,
      colorWanted?: string,
      note?: string
    ) => {
      try {
        setLoading(true)
        setError(null)
        await createNoSaleReason(sessionId, reasonCode, productName, sizeWanted, colorWanted, note)
        setLoading(false)
        return true
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao registrar motivo')
        setLoading(false)
        return false
      }
    },
    []
  )

  const batchReorderQueue = useCallback(
    async (updates: Array<{ sellerId: number; position: number }>) => {
      try {
        console.log('[useQueueActions] 🚀 batchReorderQueue START - Updates:', updates.length)
        setLoading(true)
        setError(null)
        console.log('[useQueueActions] 🔄 Calling batchUpdateQueuePositions API...')
        
        // Timeout: SE não responder em 20s, vai dar erro (Axios já tem 15s, este é safeguard)
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => {
            console.error('[useQueueActions] ⏱️ API TIMEOUT após 20s')
            reject(new Error('API timeout após 20 segundos'))
          }, 20000)
        )

        await Promise.race([
          batchUpdateQueuePositions(updates),
          timeoutPromise,
        ])

        console.log('[useQueueActions] ✅✅ API call succeeded')
        setLoading(false)
        return true
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err)
        console.error('[useQueueActions] ❌ batchReorderQueue FAILED:', errorMsg)
        setError(errorMsg)
        setLoading(false)
        return false
      }
    },
    []
  )

  return {
    loading,
    error,
    changeStatus,
    reorderQueue,
    batchReorderQueue,
    removeSellerFromQueue,
    callSeller,
    finishCall,
    addToQueue,
    finishSession,
    registerNoSaleReason,
  }
}
